/**
 * CustomerLayout - Nav for customer portal: Dashboard + Account.
 */
import { Link, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { navItemStyle, navItemHover, navItemActive } from "./ui/NavStyles";

export default function CustomerLayout({ user, signOut }) {
  const location = useLocation();
  const [authEmail, setAuthEmail] = useState("Unknown user");

  useEffect(() => {
    let mounted = true;
    async function loadAuth() {
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload || {};
        if (mounted) {
          setAuthEmail(payload.email || user?.username || "Unknown user");
        }
      } catch (err) {
        console.error("Failed to load auth session", err);
      }
    }
    loadAuth();
    return () => {
      mounted = false;
    };
  }, [user]);

  const navLink = (to, label) => {
    const isActive =
      to === "/"
        ? location.pathname === "/"
        : location.pathname === to || location.pathname.startsWith(to + "/");
    return (
      <Link
        to={to}
        style={{ ...navItemStyle, ...(isActive ? navItemActive : {}) }}
        onMouseEnter={(e) => {
          if (!isActive) Object.assign(e.target.style, navItemHover);
        }}
        onMouseLeave={(e) => {
          if (!isActive) Object.assign(e.target.style, navItemStyle);
        }}
      >
        {label}
      </Link>
    );
  };

  const initials = authEmail
    .split("@")[0]
    .split(/[.\-_]/)
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  return (
    <div>
      <header style={styles.header}>
        <nav style={styles.nav}>
          {navLink("/", "Dashboard")}
          {navLink("/room", "Room")}
          <div style={{ flex: 1 }} />
          <div style={styles.accountBox}>
            <Link
              to="/account"
              title="Account"
              style={{
                ...styles.accountLink,
                ...(location.pathname === "/account"
                  ? styles.accountLinkActive
                  : {}),
              }}
            >
              <div style={styles.avatar}>{initials}</div>
              <div style={styles.accountInfo}>
                <div style={styles.email}>{authEmail}</div>
                <div style={styles.accountHint}>Account</div>
              </div>
            </Link>
            <button onClick={signOut} style={styles.signOutButton}>
              Sign out
            </button>
          </div>
        </nav>
      </header>
      <main style={{ padding: 16 }}>
        <Outlet />
      </main>
    </div>
  );
}

const styles = {
  header: {
    padding: "10px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  accountBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#ffffff",
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#2563eb",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    flexShrink: 0,
  },
  accountLink: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    textDecoration: "none",
    color: "inherit",
    borderRadius: 6,
    padding: 2,
  },
  accountLinkActive: {
    outline: "2px solid #2563eb",
    outlineOffset: 1,
  },
  accountInfo: {
    lineHeight: 1.2,
  },
  email: {
    fontSize: 13,
    fontWeight: 500,
  },
  accountHint: {
    fontSize: 11,
    color: "#6b7280",
  },
  signOutButton: {
    marginLeft: 8,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #dc2626",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: 12,
    cursor: "pointer",
  },
};
